import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "../context/CartContext";
import logo from "../assets/logo.PNG";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300&family=Jost:wght@300;400;500&display=swap');
`;

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Portfolio", path: "/portfolio" },
  { label: "AI Art", path: "/ai-art" },
  { label: "Custom Print", path: "/custom-print" },
  { label: "About Me", path: "/about" },
];

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 72px;
  display: flex;
  align-items: center;
  padding: 0 5vw;
  justify-content: space-between;
  background: ${({ $scrolled }) =>
    $scrolled ? "rgba(8,5,2,0.72)" : "rgba(8,5,2,0.38)"};
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-bottom: 1px solid
    ${({ $scrolled }) =>
      $scrolled ? "rgba(201,169,110,0.18)" : "rgba(245,237,224,0.08)"};
  transition:
    background 0.5s ease,
    border-color 0.5s ease;
`;

const Logo = styled.button`
  display: flex;
  flex-direction: column;
  line-height: 1;
  text-decoration: none;
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  img {
    height: 140px;
    margin-top: 10px;
  }
`;

const LogoMain = styled.span`
  font-family: "Cormorant Garamond", serif;
  font-weight: 400;
  font-size: 1.45rem;
  color: #f5ede0;
  letter-spacing: 0.04em;
`;

const LogoSub = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 0.58rem;
  font-weight: 500;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: #c9a96e;
  margin-top: 2px;
`;

const LinksList = styled.ul`
  display: flex;
  gap: 2.8rem;
  list-style: none;
  margin: 0;
  padding: 0;
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLinkBtn = styled.button`
  font-family: "Jost", sans-serif;
  font-size: 0.78rem;
  font-weight: 400;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $active }) => ($active ? "#f5ede0" : "rgba(245,237,224,0.7)")};
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  padding-bottom: 3px;
  transition: color 0.3s ease;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${({ $active }) => ($active ? "100%" : "0")};
    height: 1px;
    background: #c9a96e;
    transition: width 0.35s ease;
  }

  &:hover {
    color: #f5ede0;
    &::after {
      width: 100%;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  flex-shrink: 0;
`;

const CartBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  background: transparent;
  border: none;
  color: rgba(245, 237, 224, 0.7);
  font-family: "Jost", sans-serif;
  font-size: 0.78rem;
  font-weight: 400;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  position: relative;
  padding-bottom: 3px;
  transition: color 0.3s ease;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 1px;
    background: #c9a96e;
    transition: width 0.35s ease;
  }
  &:hover {
    color: #f5ede0;
    &::after {
      width: 100%;
    }
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const CartBadge = styled.span`
  background: #c9a96e;
  color: #0a0804;
  font-size: 0.55rem;
  font-weight: 500;
  font-family: "Jost", sans-serif;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ContactBtn = styled.button`
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: 1px solid #c9a96e;
  color: #c9a96e;
  font-family: "Jost", sans-serif;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 0.62rem 1.5rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: color 0.35s ease;
  flex-shrink: 0;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: #c9a96e;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.35s ease;
  }
  span {
    position: relative;
    z-index: 1;
  }
  &:hover {
    color: #0a0804;
    &::before {
      transform: scaleX(1);
    }
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const HamburgerBtn = styled.button`
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  z-index: 110;
  @media (max-width: 768px) {
    display: flex;
  }
`;

const Bar = styled(motion.span)`
  display: block;
  width: 26px;
  height: 1.5px;
  background: #f5ede0;
  transform-origin: center;
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 99;
  background: rgba(8, 5, 2, 0.97);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const MobileLinkBtn = styled(motion.button)`
  font-family: "Cormorant Garamond", serif;
  font-weight: 300;
  font-size: clamp(2.2rem, 8vw, 3.5rem);
  color: ${({ $active }) => ($active ? "#c9a96e" : "rgba(245,237,224,0.8)")};
  background: none;
  border: none;
  cursor: pointer;
  letter-spacing: 0.02em;
  padding: 0.6rem 0;
  transition: color 0.3s;
  &:hover {
    color: #c9a96e;
  }
`;

const MobileDivider = styled.div`
  width: 40px;
  height: 1px;
  background: rgba(201, 169, 110, 0.25);
  margin: 0.4rem 0;
`;

const MobileCartBtn = styled(motion.button)`
  margin-top: 1.5rem;
  background: transparent;
  border: none;
  color: rgba(245, 237, 224, 0.6);
  font-family: "Jost", sans-serif;
  font-size: 0.78rem;
  font-weight: 400;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  transition: color 0.3s;
  &:hover {
    color: #c9a96e;
  }
`;

const MobileContact = styled(motion.button)`
  margin-top: 1rem;
  background: transparent;
  border: 1px solid #c9a96e;
  color: #c9a96e;
  font-family: "Jost", sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  padding: 0.9rem 2.4rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: color 0.35s;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: #c9a96e;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.35s;
  }
  span {
    position: relative;
    z-index: 1;
  }
  &:hover {
    color: #0a0804;
    &::before {
      transform: scaleX(1);
    }
  }
`;

const menuVariants = {
  hidden: { opacity: 0, transition: { duration: 0.3, ease: "easeInOut" } },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.35,
      ease: "easeInOut",
      staggerChildren: 0.07,
      delayChildren: 0.1,
    },
  },
  exit: { opacity: 0, transition: { duration: 0.25, ease: "easeInOut" } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: "easeOut" },
  },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { totalItems, openCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleNav = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleCartClick = () => {
    setMenuOpen(false);
    openCart();
  };

  return (
    <>
      <GlobalStyle />
      <Nav $scrolled={scrolled}>
        <Logo onClick={() => handleNav("/")}>
          <img src={logo} alt="" />
        </Logo>

        <LinksList>
          {NAV_LINKS.map(({ label, path }) => (
            <li key={label}>
              <NavLinkBtn
                $active={location.pathname === path}
                onClick={() => handleNav(path)}
              >
                {label}
              </NavLinkBtn>
            </li>
          ))}
        </LinksList>

        <Actions>
          <CartBtn onClick={handleCartClick}>
            Cart
            {totalItems > 0 && <CartBadge>{totalItems}</CartBadge>}
          </CartBtn>
          <ContactBtn>
            <span>Contact</span>
          </ContactBtn>
        </Actions>

        <HamburgerBtn
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <Bar
            animate={menuOpen ? { rotate: 45, y: 6.5 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          />
          <Bar
            animate={
              menuOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }
            }
            transition={{ duration: 0.2 }}
          />
          <Bar
            animate={menuOpen ? { rotate: -45, y: -6.5 } : { rotate: 0, y: 0 }}
            transition={{ duration: 0.3 }}
          />
        </HamburgerBtn>
      </Nav>

      <AnimatePresence>
        {menuOpen && (
          <MobileMenu
            key="mobile-menu"
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {NAV_LINKS.map(({ label, path }, i) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <MobileLinkBtn
                  $active={location.pathname === path}
                  variants={itemVariants}
                  onClick={() => handleNav(path)}
                >
                  {label}
                </MobileLinkBtn>
                {i < NAV_LINKS.length - 1 && (
                  <motion.div variants={itemVariants}>
                    <MobileDivider />
                  </motion.div>
                )}
              </div>
            ))}

            <MobileCartBtn variants={itemVariants} onClick={handleCartClick}>
              Cart {totalItems > 0 && <CartBadge>{totalItems}</CartBadge>}
            </MobileCartBtn>

            <MobileContact variants={itemVariants}>
              <span>Contact</span>
            </MobileContact>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
}
